/**
 * Phase 134C — Activation Banner
 * --------------------------------------------------------------------
 * Rendered on /livex/dashboard for academies that are verified but have
 * NOT yet broadcast their first stream after 48+ hours since verify.
 *
 * Tone is supportive + exciting (not salesy). Single primary CTA pushes
 * the academy to the studio. Once they broadcast their first stream,
 * this component stops rendering on subsequent loads (gated by
 * /api/livex/me/activation-status server-side).
 */
import { Link } from 'react-router-dom';
import { Radio, ArrowRight, Sparkles, Timer } from 'lucide-react';

export default function LivexActivationBanner({ accountAgeHours }) {
  return (
    <div
      data-testid="livex-dashboard-activation-banner"
      className="relative p-5 sm:p-6 rounded-2xl border-2 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0c1a2e 0%, #07111F 60%)',
        borderColor: '#FF5A1F',
        boxShadow: '0 10px 32px rgba(255,90,31,0.18)',
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(55% 60% at 100% 50%, rgba(255,90,31,0.22), transparent 70%)',
        }}
      />
      <div className="relative flex items-start gap-4 flex-wrap">
        <div
          className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, #dc2626, #f97316)',
            boxShadow: '0 8px 20px rgba(255,90,31,0.40)',
          }}
        >
          <Radio className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.2em] text-[#fed7aa] mb-1 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Ready when you are
          </div>
          <h3 className="text-lg sm:text-xl font-black text-white tracking-tight leading-tight">
            🎥 Ready to broadcast your first match?
          </h3>
          <p className="livex-body-text mt-1" style={{ fontSize: '13px' }}>
            Your academy is set up — go live in under 60 seconds. Add a title, tap
            <em className="text-[#FF5A1F]"> Go Live</em>, and parents can start
            watching instantly.
          </p>
          {accountAgeHours != null && accountAgeHours >= 48 && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#94A3B8]">
              <Timer className="h-3 w-3" />
              {accountAgeHours >= 168
                ? 'Your audience is waiting — let\'s get you live.'
                : 'Academies that go live in their first week see 4× parent engagement.'}
            </div>
          )}
        </div>
        <Link
          to="/streamx/studio"
          data-testid="livex-dashboard-activation-banner-cta"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
          style={{
            background: 'linear-gradient(135deg, #dc2626, #f97316)',
            boxShadow: '0 8px 24px rgba(255,90,31,0.30)',
          }}
        >
          <Radio className="h-4 w-4" /> Start Streaming
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
